// @generated-imports
import { DEFAULT_EMULATOR_BUILD, EMULATOR_BUILDS } from './shared-config.js';
// @generated-imports
/*
  Web Emulation — emutab.js
  JavaScript puro, estado centralizado, catálogo editável e integração com EmulatorJS.

  IMPORTANTE: o EmulatorJS (especialmente em /latest/) não suporta ser
  reinicializado várias vezes na mesma página — globais, AudioContext,
  workers e listeners ficam pendurados. Para trocar de ROM com segurança,
  rodamos o emulador dentro de um <iframe> (player.html). Cada novo jogo
  recria o iframe; o navegador descarta tudo do anterior.
*/

const CONFIG = {
  emulatorCore: 'nes',
  // Endpoint usado tanto aqui (preflight, etc.) quanto repassado ao iframe.
  // ATENÇÃO: prefira uma release ESTÁVEL e FIXA do EmulatorJS hospedada
  // localmente (ex.: .../emulatorjs/4.x.y/data/). Builds nightly mudam
  // internals sem aviso — variação de desempenho entre visitas e risco de
  // quebrar o watchdog de gamepad do player.html, que instrumenta métodos
  // internos pelo nome. Ao trocar aqui, troque também o fallback idêntico
  // no player.html.
  // Build do EmulatorJS: 'latest' ou 'nightly' — os endpoints vivem em
  // src/shared-config.js (allowlist única para portal e player). Trocar só
  // este valor muda a build para ROMs do catálogo e locais.
  emulatorBuild: DEFAULT_EMULATOR_BUILD,
  emulatorDataPath: EMULATOR_BUILDS[DEFAULT_EMULATOR_BUILD],
  // Página interna que efetivamente carrega o EmulatorJS dentro do iframe.
  playerPage: 'player.html',
  localStorageLastGameKey: 'nes-arcade:last-game-id',
  localStorageFavoritesKey: 'emutab:favorites',
  localStorageRecentKey: 'emutab:recent-games',
  localStorageFiltersKey: 'emutab:filters',
  localStoragePlayCountsKey: 'emutab:play-counts',
  localStorageLiteKey: 'emutab:lite',
  localStorageSettingsKey: 'emutab:settings',
  localStoragePlayTimeKey: 'emutab:play-time',
  localStorageLastBackupKey: 'emutab:last-backup-at',
  // v2.1 — chaves por perfil (profiles.js reescreve no boot quando o perfil
  // ativo não é o principal) e globais.
  localStorageLastPlayedKey: 'emutab:last-played',
  localStorageCollectionsKey: 'emutab:collections',
  localStoragePlayLaterKey: 'emutab:play-later',
  localStorageAchievementsKey: 'emutab:achievements',
  localStorageCountersKey: 'emutab:counters',
  localStorageOpenSessionKey: 'emutab:open-session',
  localStorageProfilesKey: 'emutab:profiles',          // global
  localStorageActiveProfileKey: 'emutab:active-profile', // global
  localStorageSeenVersionKey: 'emutab:seen-version',   // global
  localStorageOfflineKey: 'emutab:offline-games',      // global
  localStorageCoresReadyKey: 'emutab:cores-ready',     // global
  localStorageFolderBackupKey: 'emutab:folder-backup-at', // global
  timelineIntervalMs: 5000,
  timelineMaxSnapshots: 12,
  timelineMaxBytes: 32 * 1024 * 1024,
  appVersion: typeof __EMUTAB_VERSION__ !== 'undefined' ? __EMUTAB_VERSION__ : '2.0.0',
  backupReminderDays: 30,
  showcaseIdleMs: 180000,
  // Biblioteca de ROMs locais (File em IndexedDB) — permite "Continuar" e
  // recentes também para ROMs abertas por drag & drop / seletor de arquivo.
  // O nome NÃO casa com o padrão do backup de propósito: ROMs são grandes
  // demais para o .json de backup.
  localRomsDbName: 'emutab-local-roms',
  recentGamesLimit: 6,
  // Renderização em lotes: com 600+ jogos, criar todos os cards de uma vez
  // trava o layout e a animação escalonada demora minutos. Renderizamos em
  // páginas e um sentinel (IntersectionObserver) carrega o resto sob demanda.
  catalogPageSize: 60,
  romPreflightEnabled: true,
  romPreflightTimeoutMs: 7000,
  // Validade do preflight bem-sucedido: dentro da janela, "Jogar" de novo
  // não paga outro HEAD; passada a janela, revalida (a ROM pode ter sumido
  // do servidor no meio da sessão e o erro amigável volta a aparecer).
  romPreflightTtlMs: 600000,
  // Rearmado a cada sinal de progresso do player (loader baixado, player
  // pronto p/ receber a ROM). 20s davam falso timeout no primeiro acesso em
  // rede lenta (TV/Wi-Fi), com core wasm + ROM ainda baixando.
  loaderWatchdogMs: 45000,
  // BIOS por core para ROMs locais (drag & drop). Sistemas como 3DO,
  // Sega CD, Saturn, Amiga (Kickstart) e alguns jogos de PSX exigem BIOS;
  // sem ela o core inicia e falha. Hospede o arquivo e aponte a URL aqui:
  // Exemplo:
  //   segaCD: 'bios/bios_CD_U.bin',
  //   segaSaturn: 'bios/saturn_bios.bin',
  //   '3do': 'bios/panafz10.bin',
  //   psx: 'bios/scph5501.bin',
  //   puae: 'bios/kick34005.A500',
  localCoreBios: {},
};

const fallbackRoms = [
  {
    "id": "super-mario-bros-3",
    "title": "Super Mario Bros. 3",
    "description": "A bright platform challenge prepared for your own authorized homebrew ROM.",
    "year": "2026",
    "genre": "Platform",
    "favorite": true,
    "featured": true,
    "cover": "assets/cover/neon-plumber.jpg",
    "romUrl": "roms/mario.nes"
  },
  {
    id: 'byte-racer',
    title: 'Byte Racer DX',
    description: 'Tiny engines, sharp curves and a circuit full of electric nostalgia.',
    year: '2025',
    genre: 'Racing',
    favorite: true,
    featured: false,
    cover: 'assets/cover/byte-racer.jpg',
    romUrl: 'roms/byte-racer.nes',
  },
  {
    id: 'cosmic-dungeon',
    title: 'Cosmic Dungeon',
    description: 'A moody dungeon crawler placeholder for an authorized NES adventure.',
    year: '2024',
    genre: 'Adventure',
    favorite: false,
    featured: true,
    cover: 'assets/cover/cosmic-dungeon.jpg',
    romUrl: 'roms/cosmic-dungeon.nes',
  },
  {
    id: 'pixel-galaxy',
    title: 'Pixel Galaxy 88',
    description: 'Arcade space action with lasers, shields and a starfield ready to blink.',
    year: '2023',
    genre: 'Shooter',
    favorite: false,
    featured: false,
    cover: 'assets/cover/pixel-galaxy.jpg',
    romUrl: 'roms/pixel-galaxy.nes',
  },
  {
    id: 'temple-of-bits',
    title: 'Temple of Bits',
    description: 'Puzzle rooms, strange keys and ruins rendered in delicious 8-bit restraint.',
    year: '2022',
    genre: 'Puzzle',
    favorite: false,
    featured: false,
    cover: 'assets/cover/temple-of-bits.jpg',
    romUrl: 'roms/temple-of-bits.nes',
  },
  {
    id: 'moonlight-kunai',
    title: 'Moonlight Kunai',
    description: 'A fast action placeholder for a ninja-flavored homebrew cartridge.',
    year: '2026',
    genre: 'Action',
    favorite: true,
    featured: true,
    cover: 'assets/cover/moonlight-kunai.jpg',
    romUrl: 'roms/moonlight-kunai.nes',
  },
];

const state = {
  roms: [],        // lista EXIBIDA (derivada: catálogo + biblioteca local)
  catalog: [],     // catálogo remoto (fonte)
  bootReady: false,
  filteredRoms: [],
  selectedGame: null,
  query: '',
  genre: 'all',
  system: 'all',
  favoritesOnly: false,
  view: 'cover', // 'cover' (capa vertical) | 'screenshot' (tela horizontal)
  modalGame: null,
  modalLastFocus: null,
  favorites: new Set(),
  recentGames: [],
  playCounts: {},
  renderLimit: 0,
  sort: 'featured',
  isCatalogLoading: false,
  isGameLoading: false,
  currentIframe: null,
  gameLoadTimer: null,
  toastTimer: null,
  messageHandler: null,
  corePickerResolve: null,
  corePickerLastFocus: null,
  controlsToastShown: false,
  lastGamepadToast: '',
  lastGamepadToastAt: 0,
  frozenToastShown: false,
  focusForwarderArmed: false,
  gpNavIndex: -1,
  gpNavPrevPressed: {},
  gpNavLastMove: 0,
  gpNavPrimed: false,
  gpPickerIndex: -1,
  gpCompatToastShown: false,
  loadMoreObserver: null,
  liteMode: false,
  toastQueue: [],
  toastShowing: false,
  preflightOk: new Map(), // romUrl -> timestamp do último preflight OK
  emuDataEntries: new Map(), // id → {db, store, key} da gestão de dados
  emuEntrySeq: 0,
  emuExpandedDbs: new Set(),
  emuRenderToken: 0,         // descarta renders concorrentes obsoletos
  emuInventoryPromise: null, // contagens compartilhadas (resumo + gestão)
  gpNavPadIndex: -1,
  backupLastFocus: null,
  localRoms: new Map(),
  storagePersisted: false,
  lastGamepadName: '',
  settings: {},
  playTime: {},          // id → segundos acumulados
  sessionStartedAt: 0,
  sessionGameId: '',
  saveIndex: new Map(),  // título/arquivo normalizado → true (tem save state)
  withSaveOnly: false,
  listFilter: 'all',     // v2.1: all | play-later | offline | col:<id>
  regionFilter: 'all',   // v2.1: all | pt | <região>
  stateMeta: new Map(),  // v2.1: gameId → estado mais recente (índice leve)
  profileId: 'main',
  collectionsGame: null,
  lastDeviceCheck: null,
  playersFilter: 'all',
  session: null,
  stateSeq: 0,
  wakeLock: null,
  installPrompt: null,
  showcaseTimer: 0,
  showcaseIndex: 0,
  padLegendContext: '',
  lastQuickState: null,
  lastRandomId: '',
  helpLastFocus: null,
  settingsLastFocus: null,
};

/** Preferências do usuário (painel ⚙️). Todas ficam em emutab:settings. */
const SETTINGS_DEFS = [
  { id: 'wakeLock', label: 'Manter a tela acesa jogando', hint: 'Evita o protetor de tela em TVs — o controle não conta como atividade para o navegador.', def: true },
  { id: 'autoFullscreen', label: 'Tela cheia automática ao jogar', hint: 'Entra em tela cheia quando o jogo começa (celular e telas grandes com controle).', def: 'auto' , options: [['auto', 'Automático'], ['always', 'Sempre'], ['never', 'Nunca']] },
  { id: 'lockLandscape', label: 'Travar em paisagem no celular', hint: 'Gira e trava a tela ao iniciar um jogo — em retrato a área de jogo fica minúscula.', def: true },
  { id: 'autoSaveOnExit', label: 'Salvar estado ao desligar', hint: 'Guarda o ponto exato ao sair pelo Select+Start e oferece retomar no Continuar.', def: true },
  { id: 'confirmImport', label: 'Confirmar antes de importar backup', hint: 'Mostra uma prévia do arquivo antes de substituir seus dados.', def: true },
  { id: 'backupReminder', label: 'Lembrete de backup', hint: 'Avisa quando passar de 30 dias sem exportar, se houver saves.', def: true },
  { id: 'showcase', label: 'Modo vitrine quando ocioso', hint: 'Após 3 minutos parado no catálogo, roda um slideshow das capas.', def: false },
  { id: 'padLegend', label: 'Legenda de botões do controle', hint: 'Barra com os comandos do gamepad enquanto um controle estiver conectado.', def: true },
  { id: 'autosaveInterval', label: 'Salvar automaticamente durante o jogo', hint: 'Grava o ponto atual a cada intervalo — protege contra aba travada, bateria ou TV box reiniciando.', def: '5', options: [['0', 'Desligado'], ['3', 'A cada 3 min'], ['5', 'A cada 5 min'], ['10', 'A cada 10 min']] },
  { id: 'rewind', label: 'Voltar no tempo', hint: 'Guarda um ponto a cada 5 s (último minuto) para desfazer um erro pelo menu de pausa. Usa memória.', def: 'auto', options: [['auto', 'Automático (desliga no modo leve)'], ['on', 'Ligado'], ['off', 'Desligado']] },
  { id: 'stateSlots', label: 'Slots manuais por jogo', hint: 'Quantos slots de estado aparecem no painel ⏪ Estados.', def: '3', options: [['3', '3 slots'], ['5', '5 slots'], ['10', '10 slots']] },
  { id: 'uiScale', label: 'Tamanho da interface', hint: 'Aumenta textos e botões para ver do sofá. Automático amplia em telas grandes com controle.', def: 'auto', options: [['auto', 'Automático'], ['100', 'Normal'], ['115', 'Grande'], ['130', 'Extra grande']] },
  { id: 'askProfileOnStart', label: 'Perguntar quem vai jogar ao abrir', hint: 'Com mais de um perfil, mostra a escolha de perfil ao abrir o EmuTab.', def: true },
  { id: 'folderBackupDays', label: 'Backup automático na pasta', hint: 'Grava o backup na pasta escolhida no painel 💾 (Chrome/Edge no computador).', def: '0', options: [['0', 'Desligado'], ['1', 'Todo dia'], ['7', 'Toda semana']] },
  { id: 'askPlayerOrder', label: 'Perguntar quem é P1/P2', hint: 'Em jogos multiplayer com dois controles conectados, identifica cada jogador antes de começar.', def: true },
];

const dom = {};

// @generated-exports
export { CONFIG, SETTINGS_DEFS, dom, fallbackRoms, state };
// @generated-exports
